﻿import { compareByOrder, resultAttrInfo, resultPoints, resultValue, type FormulaCache, type PointsCache } from "@/orm/scoring"
import { DateFormat, ds } from "@/plugins/i18nFormat"
import EventBoat from "@model/EventBoat"
import EventClass from "@model/EventClass"
import EventRace from "@model/EventRace"
import { Model, useRepo } from 'pinia-orm'
import { DateCast } from "pinia-orm/casts"
import { Attr, BelongsTo, Cast, Num, Str, Uid } from 'pinia-orm/decorators'

export default class EventResult extends Model {
    static entity = 'events-results'

    @Uid() declare id: string
    @Uid() declare eventId: string      // only for sharding
    @Uid() declare eventRaceId: string
    @BelongsTo( () => EventRace, 'eventRaceId' ) declare race: EventRace
    @Uid() declare eventBoatId: string
    @BelongsTo( () => EventBoat, 'eventBoatId' ) declare boat: EventBoat
    @Num( null ) declare order: number | null
    @Str( null ) declare other: string | null
    @Num( null ) declare attr: number | null
    // Exact points to report, bypassing the order/other formula. Used by materialized elimination
    // results, whose points carry cross-group offsets that the normal formula cannot reproduce.
    @Num( null ) declare fixedPoints: number | null
    @Num( 0 ) declare counter: number
    @Num( null ) declare rounds: number | null
    @Cast( () => DateCast ) @Attr( '' ) declare finishTime: Date | null
    @Cast( () => DateCast ) @Attr( '' ) declare ts: Date | null

    get dump () {
        return {
            eventBoatId: this.eventBoatId,
            _boat: { country: this.boat?.country, sailNumber: this.boat?.sailNumber },
            order: this.order,
            other: this.other,
            attr: this.attr,
            fixedPoints: this.fixedPoints,
            counter: this.counter,
            rounds: this.rounds,
            _points: this.points,
            finishTime: this.finishTime === null ? null : ds( this.finishTime, DateFormat.System ),
            ts: this.ts === null ? null : ds( this.ts, DateFormat.System )
        }
    }

    // AI hint: invalidate cache helpers when mutable inputs change could be added via setters if needed

    get orderInclHandicap (): number | null {
        if ( !this.isHandicap )
            return this.order

        const simpleEventClass = this.boat?.eventClass ?? null

        const boatClassId = this.boat?.boatClassId
        if ( ! boatClassId )
            return this.order

        let race = this.race
        if ( race === null || race === undefined )
            race = useRepo( EventRace ).with( 'results', ( q ) => {
                q.with( 'boat' )
            } ).find( this.eventRaceId )

        const startTime = race?.startTime( boatClassId, false, this.eventBoatId ) ?? null

        if ( startTime === null )
            return this.order

        const results = ( race.results ?? [] ).filter( res => res.boat?.boatClassId === boatClassId && res.other === null )

        const ordered = results
            .map( ( item ) => {
                const itemStartTime = race?.startTime( boatClassId, false, item.eventBoatId ) ?? null

                return item.calculatedTime( itemStartTime, null, simpleEventClass )
            } )
            .sort( ( a, b ) => ( a - b ) )

        const calcTime = this.calculatedTime( startTime, null, simpleEventClass )

        const newOrder = ( ordered.indexOf( calcTime ) + ordered.lastIndexOf( calcTime ) ) / 2 + 1

        return Math.round( newOrder * 10.0 ) / 10
    }

    get isHandicap () {
        return this.finishTime !== null && this.finishTime !== undefined && !isNaN( this.finishTime.getTime() )
    }

    realTime ( startTime: Date | null = null ): number | null {
        if ( !this.isHandicap )
            return null

        if ( startTime === null ) {
            if ( this.race !== null && this.race !== undefined && this.boat !== null && this.boat !== undefined )
                startTime = this.race.startTime( this.boat.boatClassId, false, this.boat.id )
            else if ( this.boat !== null && this.boat !== undefined ) {
                const race = useRepo( EventRace ).find( this.eventRaceId )

                if ( race ) {
                    startTime = race.startTime( this.boat.boatClassId, false, this.boat.id )
                }
            }
        }

        if ( startTime === null || this.finishTime === null )
            return 0

        const raceTime = ( this.finishTime.getTime() - startTime.getTime() ) / 1000.0

        return Math.round( ( this.rounds !== null && this.rounds !== undefined ) ? raceTime / this.rounds : raceTime )
    }

    calculatedTime ( startTime: Date | null = null, coefficient: number | null = null, eventClass: EventClass | null = null ): number | null {
        const realTime = this.realTime( startTime )

        if ( realTime === null )
            return null

        if ( coefficient === null )
            coefficient = this.boat.calcHandicapCoef( eventClass, this.eventRaceId )

        return realTime * coefficient
//        return Math.round( realTime * coefficient )
    }

    get points (): number {
        return this.calculatePoints()
    }

    declare __pointsCache?: PointsCache
    static __formulaCache: FormulaCache = new Map()

    calculatePoints ( totalBoats: number | null = null ): number {
        // Materialized elimination results carry their exact (offset-adjusted) points directly.
        if ( this.fixedPoints !== null )
            return this.fixedPoints

        // The fleet size comes from the boat's class (the same source the overall results use) - the
        // result's race relation is not always loaded here, and race.totalBoats would then yield 0,
        // which would drop the penalty of "other" codes such as SCP in the per-race finish table.
        const boats = totalBoats === null
            ? ( this.boat?.eventClass?.totalBoats ?? this.race?.totalBoats( this.boat?.boatClassId ) ?? 0 )
            : totalBoats

        // Uses the handicap-adjusted order for scoring.
        const { value, cache } = resultPoints( this.order, this.orderInclHandicap, this.other, this.attr, boats, EventResult.__formulaCache, this.__pointsCache )

        this.__pointsCache = cache

        return value
    }

    get result (): string | number | null {
        return resultValue( this.other, this.orderInclHandicap )
    }

    set result ( val ) {
        // ignore
    }

    orderTo ( comparable: EventResult ) {
        if ( this.isHandicap && this.order !== null && comparable.order !== null )
            return this.finishTime > comparable.finishTime ? 1 : ( this.finishTime < comparable.finishTime ? -1 : 0 )

        return compareByOrder( this.order, comparable.order, this.other, comparable.other )
    }

    get attrInfo () {
        return resultAttrInfo( this.attr, this.other )
    }

    static piniaOptions = {
        persist: false
    }
}
